import React, { useEffect, useRef, useState } from 'react';
import { PauseOutlined, CaretRightOutlined } from '@ant-design/icons';

const formatClock = (sec) => {
  const s = Number.isFinite(sec) && sec > 0 ? sec : 0;
  return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
};

let activeAudio = null;

/**
 * پخش‌کننده جمع‌وجور پیام صوتی. صدای ضبط‌شده webm مدت ندارد، پس مدت ذخیره‌شده در پیوست استفاده می‌شود.
 */
const VoicePlayer = ({ src, duration, own = false }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [total, setTotal] = useState(duration || 0);
  const [failed, setFailed] = useState(false);

  useEffect(() => () => audioRef.current?.pause(), []);

  const ensureAudio = () => {
    if (audioRef.current) return audioRef.current;
    const audio = new Audio();
    audio.preload = 'metadata';
    audio.src = src;
    audio.addEventListener('loadedmetadata', () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) setTotal(audio.duration);
    });
    audio.addEventListener('timeupdate', () => setCurrent(audio.currentTime));
    audio.addEventListener('play', () => setPlaying(true));
    audio.addEventListener('pause', () => setPlaying(false));
    audio.addEventListener('ended', () => {
      setPlaying(false);
      setCurrent(0);
    });
    audio.addEventListener('error', () => setFailed(true));
    audioRef.current = audio;
    return audio;
  };

  const toggle = () => {
    const audio = ensureAudio();
    if (audio.paused) {
      if (activeAudio && activeAudio !== audio) activeAudio.pause();
      activeAudio = audio;
      audio.play().catch(() => setFailed(true));
    } else {
      audio.pause();
    }
  };

  const seek = (e) => {
    const audio = ensureAudio();
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (rect.right - e.clientX) / rect.width)); // RTL
    if (total) audio.currentTime = ratio * total;
  };

  const progress = total ? Math.min(100, (current / total) * 100) : 0;

  return (
    <div className="flex items-center gap-2 min-w-[200px] max-w-[260px] py-0.5" dir="rtl">
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? 'توقف' : 'پخش پیام صوتی'}
        className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 border-0 cursor-pointer ${
          own ? 'bg-white text-brand-primary' : 'bg-brand-primary text-white'
        }`}
      >
        {playing ? <PauseOutlined /> : <CaretRightOutlined />}
      </button>
      <div
        role="slider"
        aria-label="موقعیت پخش"
        aria-valuemin={0}
        aria-valuemax={Math.round(total)}
        aria-valuenow={Math.round(current)}
        tabIndex={0}
        onClick={seek}
        className={`relative flex-1 h-1 rounded cursor-pointer ${own ? 'bg-white/35' : 'bg-primary-100 dark:bg-white/10'}`}
      >
        <i
          className={`absolute top-0 bottom-0 right-0 rounded ${own ? 'bg-white' : 'bg-brand-primary'}`}
          style={{ width: `${progress}%` }}
        />
      </div>
      <span className="text-[10px] opacity-80 min-w-[32px] text-left" dir="ltr">
        {failed ? '—' : formatClock(playing || current ? current : total)}
      </span>
    </div>
  );
};

export default VoicePlayer;
